<template id="datepicker">
  <div style="display: inline-block">
    <div
      class="ui-datepicker"
      :class="{ toggleable: popup, expanded: expanded }"
      @keyup.page-up="prev('M', true)"
      @keyup.page-down="next('M', true)"
      @keyup.up="prev('w', true)"
      @keyup.down="next('w', true)"
      @keyup.home="today(true)"
    >
      <div class="ui-datepicker-calendar">
        <input
          ref="input"
          :readonly="readonly"
          :value="selected.format('YYYY-MM-DD')"
          @keyup.enter="selection($event.target.value)"
          @focus="focus"
          @blur="blur"
          v-show="showInput"
        />

        <div class="toggle-wrapper">
          <div class="heading">
            <button class="left" @click.prevent.stop="prev()">
              <b-icon icon="caret-left-fill" />
            </button>
            <div class="currentMonth">{{ currentMonth }}</div>
            <button class="right" @click.prevent.stop="next()">
              <b-icon icon="caret-right-fill" />
            </button>
          </div>

          {{ selectedMonth }}

          <div class="calendar">
            <div class="weekdays week">
              <div class="day weekHeading">
                {{ $t('Week') }}
              </div>
              <div v-for="day in weekDays" class="day" :key="day">
                {{ day }}
              </div>
            </div>
            <div class="month">
              <div v-for="(value, key) in calendarMonth" class="week" :key="key">
                <div class="day weekHeading">
                  {{ value[0].isoWeek() }}
                </div>
                <div
                  v-for="(value, key) in value"
                  class="day"
                  @click="selection(value)"
                  :class="{
                    isToday: isToday(value),
                    isSelected: isSelected(value),
                    isPadding: !isCurrentMonth(value),
                    isMarked: isMarked(value),
                  }"
                  :key="key"
                >
                  {{ value.format('DD') }}
                </div>
              </div>
            </div>
          </div>

          <button v-show="showInput" class="today" @click="today">{{ $t('Today') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DatePicker',
  data: function () {
    return {
      selectedMonth: '',
      visible: true,
      selected: moment(this.value),
      start: this.calculateStart(this.value),
      expanded: false, // only for toggleable
      blurTimer: false,
    };
  },
  props: {
    startFormat: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    value: {
      type: String,
      default: moment().format('YYYY-MM-DD'),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    popup: {
      type: Boolean,
      default: false,
    },
    markedDates: {
      type: Array,
      default: () => [],
    },
    showInput: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    todayDate() {
      return moment().format('YYYY-MM-DD');
    },
    selectedDate() {
      if (!this.selected.isValid() || this.selected.isSame('0000-01-01') || this.selected.isSame('9999-12-31')) {
        return '-';
      }
      return this.$t(this.selected.format('ddd')) + ' ' + this.selected.format('YYYY-MM-DD');
    },
    currentMonth() {
      return this.date().format('MMMM, YYYY');
    },
    weekDays() {
      var date = moment().startOf('week').isoWeekday(1);
      var days = [];
      for (var i = 0; i < 7; i++) {
        days.push(this.$t(date.format('ddd')));
        date.add(1, 'day');
      }
      return days;
    },
    months() {
      return moment.monthsShort();
    },
    calendarMonth() {
      var start = this.date().startOf('month').startOf('isoWeek');
      var end = this.date().endOf('month').endOf('isoWeek').add(14, 'day');
      // var range = moment().range(start, end);
      var daysList = [];
      while (start.isBefore(end)) {
        daysList.push(moment(start));
        start.add(1, 'day');
      }

      // create array
      var month = new Array(6);

      // Create empty data array form rows & columns
      for (var row = 0; row < 6; row++) {
        month[row] = new Array(7);
      }
      // Populate data in empty array
      var dayIndexCount = 0;
      for (row = 0; row < 6; row++) {
        for (var column = 0; column < 7; column++) {
          month[row][column] = daysList[dayIndexCount];
          dayIndexCount++;
        }
      }

      return month;
    },
  },
  methods: {
    // start date
    date() {
      return moment(this.calculateStart(this.start)); // moment(this.start, this.startFormat);
    },
    next(type = 'M', select = false) {
      // Type is M from Month
      var m = this.date().add(1, type);
      // Start is the first day of the week in the month
      // startFormat is the prop that defines the date dormat (yyyymmdd)
      this.start = m.format(this.startFormat);
      // if selected true select date
      if (select) {
        this.selected = m;
      }
      this.focus();
    },
    today(select = false) {
      var m = moment();
      this.start = m.format(this.startFormat);
      if (select) {
        this.selected = m;
      }
    },
    prev(type = 'M', select = false) {
      var m = this.date().subtract(1, type);
      this.start = m.format(this.startFormat);
      if (select) {
        this.selected = m;
      }
      this.focus();
    },
    selection(day) {
      this.$refs.input.focus();
      if (this.readonly) {
        return;
      }
      day = moment(day);

      if (!day.isValid()) {
        return;
      }
      this.selected = day;
      this.start = day;
      document.activeElement.blur();
      this.$emit('input', day.format('YYYY-MM-DD'));
    },
    isToday(day) {
      return day.isSame(moment(), 'day');
    },
    isMarked(day) {
      return this.markedDates.includes(day.format('YYYY-MM-DD'));
    },
    isCurrentMonth(day) {
      return day.isSame(this.start, 'month');
    },
    isSelected(day) {
      return day.isSame(this.selected, 'day');
    },
    calculateStart(date) {
      if (date === '' || date === '0000-01-01' || date === '9999-12-31') {
        return moment().format(this.startFormat);
      }
      return moment(date).format(this.startFormat);
    },
    focus() {
      if (this.popup && !this.disabled && !this.readonly) {
        clearTimeout(this.blurTimer);
        this.expanded = true;
        this.$refs.input.focus();
      }
    },
    blur() {
      if (this.popup && !this.readonly) {
        // Delay the input enough to
        this.blurTimer = setTimeout(() => {
          this.expanded = false; /* this.$emit('input', this.$refs.input.value); */
        }, 150);
      }
    },
  },
  watch: {
    value: function (value) {
      if (value === '') {
        value = '0000-01-01';
      }
      this.selected = moment(value);
      this.start = this.calculateStart(this.selected);
    },
  },
};
</script>

<style scoped>
input {
  display: block;
  margin: auto auto;
  width: 8em;
  border: 1px solid #ccc;
  padding: 2px 2px;
  border-radius: 2px;
  text-align: center;
}

.today {
  display: block;
  margin: auto auto;
  width: 8em;
  border: 1px solid black;
  padding: 0px 2px;
  border-radius: 5px;
  text-align: center;
  background-color: #000;
  color: #fff;
}

.ui-datepicker {
  background-color: #fff;
  color: #000;
  padding: 10px;
  cursor: pointer;
}

.ui-datepicker-selected {
  text-align: center;
}

.ui-datepicker .ui-datepicker-calendar .heading {
  display: flex;
  justify-content: space-between;
}

.ui-datepicker .ui-datepicker-calendar .heading .currentMonth {
  align-self: center;
}

.ui-datepicker .ui-datepicker-calendar .heading button {
  cursor: pointer;
  padding: 0;
  border: none;
}

.ui-datepicker .ui-datepicker-calendar .heading button {
  color: #000;
  font-size: 32px;
}

.ui-datepicker .ui-datepicker-calendar .calendar {
  text-align: center;
}

.ui-datepicker .ui-datepicker-calendar .calendar .weekdays {
  display: flex;
  font-size: 12px;
  color: #000;
}

.ui-datepicker .ui-datepicker-calendar .calendar .week {
  display: flex;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.ui-datepicker .ui-datepicker-calendar .calendar .weekHeading {
  border-right: 1px solid white;
  margin-right: 5px;
  width: 4em !important;
}

.ui-datepicker .ui-datepicker-calendar .calendar .day {
  color: #000;
  padding: 4px;
  font-size: 14px;
  width: 2em;
  height: 2em;
  display: flex;
  align-items: center;
  justify-content: center;
}

.isMarked {
  background-color: rgb(200, 132, 132);
  border-radius: 100%;
}

.ui-datepicker .ui-datepicker-calendar .calendar .day.isToday {
  font-weight: 700;
  position: relative;
}

.ui-datepicker .ui-datepicker-calendar .calendar .day.isToday:after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 3px;
  right: 3px;
  height: 4px;
  border-radius: 3px;
  background-color: #23a96e;
}

.ui-datepicker .ui-datepicker-calendar .calendar .month .week .day.isSelected {
  background-color: #000;
  color: #fff;
  border-radius: 50%;
}

.ui-datepicker .ui-datepicker-calendar .calendar .month .week .day.isPadding {
  color: #b2b2b2;
}

.toggle-wrapper {
  display: block;
}
.ui-datepicker.toggleable {
  position: relative;
  padding: 0;
}
.ui-datepicker.toggleable .toggle-wrapper {
  display: none;
  position: absolute;
  background: #fff;
  border: 1px solid #dadada;
  right: 0;
  padding: 10px;
  margin-top: 4px;
  z-index: 100;
}
.ui-datepicker.toggleable.expanded .toggle-wrapper {
  display: block;
}
</style>



// WEBPACK FOOTER //
// src/components/datepicker/DatePicker.vue